/**
 * Apps Hook (PostgreSQL)
 *
 * Replaces Convex api.admin.list for apps
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = '/api/apps';

interface App {
  id: string;
  name: string;
  description?: string;
  url: string;
  iconUrl?: string;
  category: string;
  roles: string[];
  sortOrder: number;
  isActive: boolean;
}

interface AppsResponse {
  data: App[];
  total: number;
}

/**
 * Fetch all apps (active only by default)
 */
export function useApps(options?: { includeInactive?: boolean }) {
  const { includeInactive = false } = options || {};

  return useQuery({
    queryKey: ['apps', { includeInactive }],
    queryFn: async (): Promise<AppsResponse> => {
      const url = includeInactive ? `${API_BASE}?all=true` : API_BASE;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Failed to fetch apps: ${res.status}`);
      }
      return res.json();
    },
    staleTime: 60 * 60 * 1000, // 1 hour - apps rarely change
  });
}

interface CreateAppInput {
  name: string;
  url: string;
  category: string;
  description?: string;
  createdBy: string;
}

interface UpdateAppInput {
  id: string;
  name?: string;
  url?: string;
  category?: string;
  description?: string;
  isActive?: boolean;
  userEmail?: string;
}

/**
 * Create a new app
 */
export function useCreateApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateAppInput): Promise<App> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', ...input }),
      });
      if (!res.ok) {
        throw new Error(`Failed to create app: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

/**
 * Update an existing app
 */
export function useUpdateApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateAppInput): Promise<{ success: boolean }> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', ...input }),
      });
      if (!res.ok) {
        throw new Error(`Failed to update app: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}

/**
 * Delete an app
 */
export function useDeleteApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userEmail }: { id: string; userEmail?: string }): Promise<{ success: boolean }> => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', id, userEmail }),
      });
      if (!res.ok) {
        throw new Error(`Failed to delete app: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });
}
